import type * as ents from '@attain-apps/entities'

import type { Paster } from './paster'

/**
 * Holds most of the {@link Entity} data. Saved as class instance to Entity
 * Collection.
 *
 * NOTE: It is class instead of interface because of the filtering by class
 * instance type. Needed for UI representation of data.
 */
export abstract class Item<TPayload> {
  /**
   * TODO:
   */
  public static paster<TItem extends Item<any>>(
    cb: Paster<TItem>['apply'],
  ): Paster<TItem> {
    return {
      // eslint-disable-next-line @typescript-eslint/promise-function-async
      apply(items) {
        return cb(items.filter(item => this.canPaste(item)))
      },
      canPaste: (item): item is TItem => item instanceof this,
    }
  }

  /**
   * @see {@link Item.Context}
   */
  public readonly context: Item.Context
  /**
   * Actual item data (e.g. entity we copied)
   */
  public readonly payload: TPayload

  constructor(_params: Readonly<Item.constructor.Params<TPayload>>) {
    this.context = _params.context
    this.payload = _params.payload
  }

  /**
   * Produces a name for a stored {@link Item}. Since the Clipboard is flexible
   * and can accept various classes or entities, each class extending this one
   * must implement logic to determine its name.
   */
  public abstract getName(): string
}
export namespace Item {
  export namespace constructor {
    export interface Params<TPayload> {
      /**
       * Item {@link Context}
       *
       * Provides context for an item stored in the clipboard collection. It can
       * offer additional information about the source of the copied item or be
       * used to group items within the same context.
       *
       * @example
       * // If Scenario holds current item, context should have:
       * const context = {
       *  tenantId
       *  zoneId
       *  appId
       *  scenarioId
       * }
       */
      context: Item.Context
      /**
       * Actual item data (e.g. entity we copied). Usually entity from other
       * entity collection
       */
      payload: TPayload
    }
  }

  /** TODO: */
  export interface Identified<TItem extends Item<any> = Item<unknown>>
    extends ents.core.common.Identified<string> {
    item: Item<TItem>
  }

  export type Context = ents.core.admin.Context.Require<'tenantId'> | undefined
  /**
   * Class reference used to create and filter clipboard items
   */
  export type Class<TItem extends Item<any> = Item<unknown>> = (new (
    params: constructor.Params<TItem['payload']>,
  ) => TItem) &
    StaticProperties<typeof Item>
}
